import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Table, Pagination, message, Tag, Spin, Popconfirm } from "antd";
import { del, get } from "../../../services";
import {
  DeleteFilled,
  DownloadOutlined,
  FunnelPlotFilled,
} from "@ant-design/icons";
import FilterModal from "./filter-modal";
import { CSVLink } from "react-csv";

const EventContacts = () => {
  const [eventsData, setEventsData] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [searchedFilter, setSearchFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loader, setLoader] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const formRef = useRef(null);
  const limit = 10;

  const getAllEvents = useCallback(
    (currentPage) => {
      setLoader(true);
      const params = new URLSearchParams(location.search);
      const query = params.toString();
      const search = query ? `&${query}` : "";

      get(`/events?page=${currentPage}&limit=${limit}&eventType=Dubai-Property-Show-2025${search}`)
        .then((response) => {
          const data = response?.data || {};
          setEventsData(data?.events || []);
          setCurrentPage(data?.currentPage || 1);
          setTotalPages(data?.totalPages || 0);
        })
        .catch(() => message.error("Failed to fetch events"))
        .finally(() => setLoader(false));
    },
    [location.search]
  ); // dependencies here

  useEffect(() => {
    getAllEvents(currentPage);
  }, [getAllEvents, currentPage]);

  const handleFilter = (values) => {
    const filterParams = Object.entries(values)
      .filter(([_, value]) => value)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join("&");

    if (filterParams) {
      navigate(`/event-contacts?${filterParams}`);
      setSearchFilter(filterParams);
    }
    setIsFilterModalOpen(false);
  };

  const handleClear = () => {
    navigate(`/event-contacts`);
    setSearchFilter("");
    if (formRef.current) formRef.current.resetFields();
  };

  const confirm = (data) => {
    setSelectedRecord(data?._id);
    del(`/events/${data?._id}`)
      .then(() => {
        const remaining = eventsData.filter((item) => item?._id !== data?._id);
        setEventsData(remaining);
        message.success("Record deleted successfully");
      })
      .catch(() => message.error("Error deleting record"))
      .finally(() => setSelectedRecord(false));
  };

  const headers = [
    { label: "Name", key: "name" },
    { label: "Email", key: "email" },
    { label: "Phone Number", key: "phoneNumber" },
    { label: "Whatsapp Number", key: "whatsappNumber" },
    { label: "Selected Dates", key: "selectedDates" },
    { label: "Selected Times", key: "selectedTimes" },
  ];

  const csvData = eventsData.map((item) => ({
    name: item?.name || "",
    email: item?.email || "",
    phoneNumber: item?.phoneNumber || "",
    whatsappNumber: item?.whatsappNumber || "",
    selectedDates:
      item?.selectedDates
        ?.map((d) => new Date(d).toLocaleDateString())
        .join(", ") || "",
    selectedTimes: item?.selectedTimes?.join(", ") || "",
  }));

  const columns = [
    {
      title: "Sl No",
      render: (_, __, index) => (currentPage - 1) * limit + index + 1,
    },
    { title: "Name", dataIndex: "name" },
    { title: "Email", dataIndex: "email" },
    { title: "Phone Number", dataIndex: "phoneNumber" },
    { title: "Whatsapp Number", dataIndex: "whatsappNumber" },
    {
      title: "Selected Dates",
      dataIndex: "selectedDates",
      render: (dates) =>
        dates?.length ? (
          dates.map((d, i) => (
            <Tag color="green" key={i} style={{ marginBottom: 4 }}>
              {new Date(d).toLocaleDateString()}
            </Tag>
          ))
        ) : (
          <span>N/A</span>
        ),
    },
    {
      title: "Selected Times",
      dataIndex: "selectedTimes",
      render: (times) =>
        times?.length ? (
          times.map((t, i) => (
            <Tag color="blue" key={i} style={{ marginBottom: 4 }}>
              {t}
            </Tag>
          ))
        ) : (
          <span>N/A</span>
        ),
    },
    {
      title: "Action",
      key: "action",
      render: (data) => (
        <div className="flex px-4">
          {data?._id === selectedRecord ? (
            <Spin />
          ) : (
            <Popconfirm
              title="Delete Record"
              description="Are you sure you want to delete this record?"
              onConfirm={() => confirm(data)}
              okText="Yes"
              cancelText="No"
            >
              <DeleteFilled className="cursor-pointer delete-icon-admin" />
            </Popconfirm>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="head_dashboard">
      <div className="header4 flex justify-between items-center mb-3">
        <h3>Events Contacts</h3>
        <div className="events-button">
          <CSVLink
            data={csvData}
            headers={headers}
            filename="event_contacts.csv"
            className="ant-btn ant-btn-primary flex items-center gap-2"
          >
            <button className="cursor-pointer add-event">
              {" "}
              <DownloadOutlined /> <span className="px-2">
                Download CSV
              </span>{" "}
            </button>
          </CSVLink>

          <button
            className="cursor-pointer flex items-center gap-1"
            onClick={() => setIsFilterModalOpen(true)}
          >
            <FunnelPlotFilled />
            <span>Filter</span>
          </button>

          {searchedFilter && (
            <button
              className="cursor-pointer ml-3 filter-clear-button"
              onClick={handleClear}
            >
              Clear
            </button>
          )}
        </div>
      </div>
      {/* Filter Modal */}
      <FilterModal
        isFilterModalOpen={isFilterModalOpen}
        setIsFilterModalOpen={setIsFilterModalOpen}
        handleFilter={handleFilter}
        formRef={formRef}
      />
      <Table
        columns={columns}
        dataSource={eventsData}
        loading={loader}
        pagination={false}
        scroll={{ x: 1024 }}
        rowKey={(record) => record?._id}
      />

      {totalPages > 1 && (
        <Pagination
          pageSize={limit}
          current={currentPage}
          total={totalPages * limit}
          showSizeChanger={false}
          onChange={(page) => getAllEvents(page)}
          className="pagination mt-4"
        />
      )}
    </div>
  );
};

export default EventContacts;
